import React, { useState, useRef } from 'react';
import { vendorUploadFinal } from '../../services/liveStamping';
import { formatDateTimeIST } from '../../utils/dateUtils';

const VendorFinalUpload = ({ order, onSuccess, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const isGated = order.status !== 'compliance_check';
  const isValid = selectedFile && !isGated;

  const handleFileSelect = (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      setError('Invalid file type. Please upload PDF, JPEG, or PNG.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }
    setError('');
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setUploading(true);
    setError('');
    setProgress(10);

    try {
      // Use XHR for progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const form = new FormData();
        form.append('file', selectedFile);

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 80) + 10);
        });
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            try {
              const err = JSON.parse(xhr.responseText);
              reject(new Error(err.detail?.message || err.detail || 'Upload failed'));
            } catch {
              reject(new Error('Upload failed'));
            }
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.open('POST', `${baseUrl}/api/external/v1/live-stamping/orders/${order.id}/upload`);
        xhr.withCredentials = true;
        xhr.send(form);
      });

      setProgress(100);
      setTimeout(() => onSuccess(), 600);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
      setUploading(false);
      setProgress(0);
    }
  };

  const meta = order.stamp_metadata || {};

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
            aria-label="Back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h3 className="font-bold text-gray-800">Stage 2 — Upload Final Document</h3>
            <p className="text-xs text-gray-500 mt-0.5">Order #{order.id} · {order.service_name}</p>
          </div>
        </div>

        {/* State gate warning */}
        {isGated && (
          <div className="mx-6 mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            Final document upload is only available after admin approves the preview. Current status: <span className="font-semibold">{order.status?.replace(/_/g, ' ')}</span>.
          </div>
        )}

        {/* Read-only summary */}
        <div className="px-6 pt-4 space-y-3">
          <div className="bg-gray-50 rounded-lg p-3 border text-sm space-y-1">
            <p><span className="font-medium text-gray-600">Client:</span> {order.client_name || '—'}</p>
            {meta.certificate_number && (
              <p><span className="font-medium text-gray-600">Certificate #:</span> {meta.certificate_number}</p>
            )}
            {meta.issue_date && (
              <p><span className="font-medium text-gray-600">Issue date:</span> {formatDateTimeIST(meta.issue_date)}</p>
            )}
          </div>

          {order.preview_base64 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Approved preview</p>
              <img
                src={`data:${order.preview_content_type || 'image/png'};base64,${order.preview_base64}`}
                alt="Approved preview"
                className="max-h-32 rounded border border-gray-200 shadow-sm object-contain"
              />
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Dropzone */}
        <div className="px-6 py-4">
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={e => handleFileSelect(e.target.files[0])}
            disabled={isGated}
          />
          <div
            onClick={() => !isGated && fileInputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { if (!isGated) handleDrop(e); }}
            className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center transition-colors ${
              isGated
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                : 'border-indigo-200 bg-indigo-50/50 cursor-pointer hover:bg-indigo-50'
            }`}
          >
            <svg className="w-10 h-10 text-indigo-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {selectedFile ? (
              <div className="text-center">
                <p className="font-medium text-indigo-900 text-sm">{selectedFile.name}</p>
                <p className="text-xs text-gray-400 mt-1">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-indigo-700">Click or drag to upload final document</p>
                <p className="text-xs text-gray-400 mt-1">PDF, JPEG, PNG · max 10MB</p>
              </>
            )}
          </div>

          {/* Progress bar */}
          {uploading && (
            <div className="mt-3">
              <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1 text-center">{progress}%</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || uploading}
            className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
            {uploading ? 'Uploading…' : 'Submit Final Document'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorFinalUpload;
